import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { toPng } from "html-to-image";
import ScreenshotLangPicker from "../../components/ScreenshotLangPicker";
import { appendScreenshotLang, useScreenshotTranslation } from "../../lib/screenshotI18n";
import { JIGSAW_MOBILE_TITLE, screenshotPath } from "../../prototype/mobile-jigsaw/routes";
import {
    isScreenshotSceneConfigured,
    JIGSAW_SCREENSHOT_SCENES,
} from "../../prototype/mobile-jigsaw/screenshotScenes";
import { downloadPngDataUrl } from "./downloadPngDataUrl";

/** Amazon Appstore tablet screenshot size (supported tablet resolution). */
const TABLET_W = 1024;
const TABLET_H = 600;
const PREVIEW_MAX_W = 320;

const TO_PNG_OPTS = {
    cacheBust: true,
    pixelRatio: 1,
    skipFonts: true,
};

const PUBLIC = process.env.PUBLIC_URL || "";
const ICON_512_SRC = `${PUBLIC}/ad/jigsaw-muse-app-icon-512.png`;
const PROMO_SRC = `${PUBLIC}/ad/jigsaw-muse-feature-graphic-1024x500.png`;

function screenshotPageHref(path, lang) {
    if (typeof window === "undefined") return path;
    const withLang = appendScreenshotLang(path, lang);
    const url = new URL(withLang, window.location.origin);
    url.searchParams.set("googleAdLandscape", "1");
    return url.toString();
}

async function loadImage(src) {
    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = () => reject(new Error("Failed to decode image"));
        img.src = src;
    });
    return img;
}

async function downloadStaticImage(src, fileName) {
    const res = await fetch(src);
    if (!res.ok) throw new Error("Failed to fetch image");
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = fileName;
        link.rel = "noopener";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } finally {
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
    }
}

async function downloadResizedPng(src, size, fileName) {
    const img = await loadImage(src);
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas unavailable");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, size, size);
    await downloadPngDataUrl(canvas.toDataURL("image/png"), fileName);
}

async function captureLandscapePng(iframe, width, height) {
    const doc = iframe?.contentDocument;
    if (!doc?.documentElement) {
        throw new Error("Preview still loading — try again in a moment");
    }
    const frame =
        doc.querySelector('[data-google-ad-landscape="1"]') || doc.documentElement;
    return toPng(frame, {
        ...TO_PNG_OPTS,
        width,
        height,
    });
}

function DownloadButton({ onClick, saving, disabled, label = "Download" }) {
    return (
        <Button
            size="small"
            variant="contained"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon />}
            onClick={onClick}
            disabled={saving || disabled}
            sx={{ minHeight: 36, textTransform: "none", fontWeight: 700 }}
        >
            {saving ? "Saving…" : label}
        </Button>
    );
}

function DropSlot({ width, height, children, caption }) {
    const maxW = Math.min(width, 280);
    const scale = maxW / width;
    return (
        <Box>
            <Box
                sx={{
                    width: Math.round(width * scale),
                    height: Math.round(height * scale),
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                    bgcolor: "action.hover",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {children}
            </Box>
            {caption ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    {caption}
                </Typography>
            ) : null}
        </Box>
    );
}

function StaticAssetCard({ title, caption, src, previewSize, onDownload, saving, error }) {
    return (
        <Stack spacing={0.75} sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {title}
            </Typography>
            <DropSlot width={previewSize} height={previewSize} caption={caption}>
                <Box
                    component="img"
                    src={src}
                    alt=""
                    sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
            </DropSlot>
            <DownloadButton onClick={onDownload} saving={saving} />
            {error ? (
                <Typography variant="caption" color="error">
                    {error}
                </Typography>
            ) : null}
        </Stack>
    );
}

function ScreenshotTile({ scene, lang }) {
    const iframeRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [iframeReady, setIframeReady] = useState(false);
    const href = screenshotPageHref(screenshotPath(scene.slug), lang);
    const previewScale = Math.min(1, PREVIEW_MAX_W / TABLET_W);
    const previewW = Math.round(TABLET_W * previewScale);
    const previewH = Math.round(TABLET_H * previewScale);

    useEffect(() => {
        setIframeReady(false);
    }, [href]);

    const handleDownload = useCallback(async () => {
        if (saving) return;
        setSaving(true);
        setSaveError(null);
        try {
            const dataUrl = await captureLandscapePng(iframeRef.current, TABLET_W, TABLET_H);
            await downloadPngDataUrl(
                dataUrl,
                `jigsaw-muse-amazon-tablet-${scene.slug}-${TABLET_W}x${TABLET_H}.png`,
            );
        } catch (e) {
            setSaveError(e?.message || "Failed to download PNG");
        } finally {
            setSaving(false);
        }
    }, [saving, scene.slug]);

    return (
        <Box sx={{ flex: `0 0 ${previewW}px`, width: previewW, mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25, fontSize: 12 }}>
                Screenshot {scene.slug}
            </Typography>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.75, minHeight: 28, fontSize: 11, lineHeight: 1.25 }}
            >
                {scene.label}
            </Typography>
            <Box
                sx={{
                    width: previewW,
                    height: previewH,
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: 1,
                    border: "1px dashed",
                    borderColor: "divider",
                    boxShadow: 1,
                    background: "#0f172a",
                }}
            >
                <Box
                    sx={{
                        width: TABLET_W,
                        height: TABLET_H,
                        transform: `scale(${previewScale})`,
                        transformOrigin: "top left",
                        overflow: "hidden",
                        position: "relative",
                        background: "#0f172a",
                    }}
                >
                    <Box
                        component="iframe"
                        ref={iframeRef}
                        src={href}
                        title={`Amazon tablet screenshot ${scene.slug}`}
                        loading="lazy"
                        onLoad={() => {
                            window.setTimeout(() => setIframeReady(true), 2200);
                        }}
                        sx={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            width: TABLET_W,
                            height: TABLET_H,
                            border: 0,
                            pointerEvents: "none",
                            display: "block",
                            background: "#0f172a",
                        }}
                    />
                </Box>
            </Box>
            <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                <DownloadButton onClick={handleDownload} saving={saving} disabled={!iframeReady} />
                {saveError ? (
                    <Typography variant="caption" color="error">
                        {saveError}
                    </Typography>
                ) : null}
            </Stack>
        </Box>
    );
}

/**
 * Admin page — Amazon Appstore tablet assets for Jigsaw Muse (1024×600 screenshots).
 */
export default function JigsawAmazonTabletPage() {
    const { lang } = useScreenshotTranslation();
    const scenes = useMemo(
        () => JIGSAW_SCREENSHOT_SCENES.filter(isScreenshotSceneConfigured),
        [],
    );
    const [iconSaving, setIconSaving] = useState(null);
    const [iconError, setIconError] = useState(null);
    const [promoSaving, setPromoSaving] = useState(false);
    const [promoError, setPromoError] = useState(null);

    const downloadIcon512 = useCallback(async () => {
        setIconSaving(512);
        setIconError(null);
        try {
            await downloadResizedPng(ICON_512_SRC, 512, "jigsaw-muse-amazon-icon-512x512.png");
        } catch (e) {
            setIconError(e?.message || "Failed to download icon");
        } finally {
            setIconSaving(null);
        }
    }, []);

    const downloadIcon114 = useCallback(async () => {
        setIconSaving(114);
        setIconError(null);
        try {
            await downloadResizedPng(ICON_512_SRC, 114, "jigsaw-muse-amazon-icon-114x114.png");
        } catch (e) {
            setIconError(e?.message || "Failed to download icon");
        } finally {
            setIconSaving(null);
        }
    }, []);

    const downloadPromo = useCallback(async () => {
        setPromoSaving(true);
        setPromoError(null);
        try {
            await downloadStaticImage(PROMO_SRC, "jigsaw-muse-amazon-promo-1024x500.png");
        } catch (e) {
            setPromoError(e?.message || "Failed to download promotional image");
        } finally {
            setPromoSaving(false);
        }
    }, []);

    return (
        <Box sx={{ p: 2, pb: 6, maxWidth: 1400, mx: "auto" }}>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
                {JIGSAW_MOBILE_TITLE} — Amazon Appstore
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Tablet assets · screenshots {TABLET_W}×{TABLET_H} PNG (portrait or landscape) · no
                video
            </Typography>
            <ScreenshotLangPicker sx={{ mb: 2 }} />

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 3,
                    mb: 3,
                }}
            >
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.25 }}>
                        Icons
                    </Typography>
                    <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                        <StaticAssetCard
                            title="512×512"
                            caption="512 × 512px PNG (with transparency)"
                            src={ICON_512_SRC}
                            previewSize={120}
                            onDownload={downloadIcon512}
                            saving={iconSaving === 512}
                            error={iconSaving === 512 ? iconError : null}
                        />
                        <StaticAssetCard
                            title="114×114"
                            caption="114 × 114px PNG (with transparency)"
                            src={ICON_512_SRC}
                            previewSize={72}
                            onDownload={downloadIcon114}
                            saving={iconSaving === 114}
                            error={iconSaving === 114 ? iconError : null}
                        />
                    </Stack>
                    {iconError && iconSaving == null ? (
                        <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
                            {iconError}
                        </Typography>
                    ) : null}
                </Box>
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.25 }}>
                        Promotional image (optional)
                    </Typography>
                    <Box
                        sx={{
                            maxWidth: 360,
                            aspectRatio: "1024 / 500",
                            border: "1px dashed",
                            borderColor: "divider",
                            borderRadius: 1,
                            overflow: "hidden",
                            bgcolor: "action.hover",
                            mb: 0.75,
                        }}
                    >
                        <Box
                            component="img"
                            src={PROMO_SRC}
                            alt=""
                            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                        />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                        1024 × 500px (landscape only) PNG or JPG
                    </Typography>
                    <DownloadButton onClick={downloadPromo} saving={promoSaving} />
                    {promoError ? (
                        <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                            {promoError}
                        </Typography>
                    ) : null}
                </Box>
            </Box>

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                Screenshots (minimum 3)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {TABLET_W} × {TABLET_H}px — PNGs (landscape) · download under each preview
            </Typography>
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.5,
                    alignItems: "flex-start",
                }}
            >
                {scenes.map((scene) => (
                    <ScreenshotTile key={scene.slug} scene={scene} lang={lang} />
                ))}
            </Box>
        </Box>
    );
}
